// PR-P0-5 reference: UC-5 (Questionnaires — LLM answer drafter) · ADR-0001, ADR-0004 · docs/feature-status-and-gaps.md §2.5
// Mockup: .claude/skills/ux-adversary/runs/uc-5-questionnaires/
// Systems Architecture: §5.13
import { defineTemplate, derive } from '../egress/data-classes.js';
import { QuestionnaireService } from './questionnaire-service.js';
import { deriveFreshness, listBindingSummariesForItem, rollUpMaturityForItem, } from './evidence-service.js';
// P8 / M4 — draft stage. Per-question LLM generation from the register-item
// citations produced by M3's match stage, with the D4 disclaimer for
// no-match answers.
// Batch this many questions per LLM call. Small batches keep the prompt
// short and the schema simple; total-cost lever.
export const DRAFT_BATCH_SIZE = 5;
// Plan §D4: the honest disclaimer for questions no register item cleanly
// addresses. Kept as a const so the exact text is version-controlled.
export const NO_MATCH_DISCLAIMER = "No register item cleanly addresses this. Closest matches: none above the confidence threshold. Preparer to answer directly or add a new register item.";
export const DRAFT_INSTRUCTIONS = [
    'You are drafting compliance-questionnaire responses.',
    'For each question, write a 1-3 sentence answer grounded in the cited register items and their evidence.',
    'Reference each cited item inline as [RR-nnn] using its risk_item_id.',
    'When a cited item carries evidence artifacts, cite them specifically by title and version_label (e.g. "documented in Fairness Testing Methodology v3.2, last attested 2026-Q2").',
    'When a relevant_excerpt is provided for an evidence artifact, incorporate the quoted passage naturally into the response to ground the claim in the source document (e.g. "per Section 4.2: \'The organization shall maintain...\'").',
    'If an evidence artifact is marked freshness="overdue", explicitly note the staleness in the sentence (e.g. "last attested 2025-Q4 — review overdue").',
    'If a cited item has maturity_override=true, mention that the effective maturity is a documented override rather than derived from stored evidence.',
    'Do not invent artifact titles, versions, or attestation dates that were not provided in the payload.',
    'Do not invent claims outside the register items. If the items only partially cover the question, say so explicitly (e.g. "Partial coverage — see RR-042 for the policy foundation; deployment attestation TBD.").',
    'Voice: matter-of-fact, no marketing language, no first-person plural pronouns like "we understand" or "we strive".',
].join(' ');
/** ADR-0073: reviewed static template; the per-batch payload is the classified input. */
export const DRAFT_TEMPLATE = defineTemplate('questionnaire.draft', 'v1', DRAFT_INSTRUCTIONS, []);
export const DRAFT_SCHEMA = {
    results: [
        { question_id: 'string', draft_text: 'string (1-3 sentences with inline [RR-nnn] citations)' },
    ],
};
// How many bindings per cited item to hand to the LLM. Two keeps the
// prompt short while still letting the model quote a policy + a
// monitoring signal in the same sentence (§0 draft example).
export const EVIDENCE_CITATIONS_PER_ITEM = 2;
/**
 * QuestionnaireDrafter — plan §6 M4.
 *
 * Reads answers from a questionnaire in status='drafting' (populated by the
 * matcher). For each answer:
 *   - No citations (D4 no-match path): stamp NO_MATCH_DISCLAIMER as the draft.
 *   - Has citations: batch with peers, ask the LLM to generate a 1-3 sentence
 *     answer per question referencing the cited register items inline.
 *
 * Persists `draft_text` + `drafted_by='llm'`; advances the questionnaire to
 * status='in_review'. Answers whose LLM draft failed or came back empty
 * fall back to a "Preparer to draft manually" placeholder so the review
 * screen still shows every question.
 */
export class QuestionnaireDrafter {
    db;
    llm;
    constructor(db, llm) {
        this.db = db;
        this.llm = llm;
    }
    async process(questionnaireId) {
        const svc = new QuestionnaireService(this.db);
        const row = await svc.get(questionnaireId);
        if (!row)
            throw new Error(`Drafter: no questionnaire ${questionnaireId}`);
        if (row.status !== 'drafting') {
            throw new Error(`Drafter: questionnaire ${questionnaireId} is in status ${row.status}, not 'drafting'`);
        }
        const rows = await this.loadAnswers(questionnaireId);
        if (rows.length === 0) {
            // Nothing to draft — advance so the pipeline doesn't stall. Match
            // must have skipped every question.
            await svc.updateStatus(questionnaireId, 'in_review');
            return { drafts: [], no_match_count: 0 };
        }
        // Load every cited register item in one unscoped read (master data,
        // workspace-agnostic). Duplicated ids across answers get one lookup.
        const citedIds = new Set();
        for (const r of rows) {
            for (const c of r.citations)
                citedIds.add(c.risk_item_id);
        }
        const itemLookup = await this.loadRiskItems([...citedIds]);
        // E8 — per-cited-item evidence snapshot. Requires the workspace's
        // profile_id; skipped (evidence map stays empty) when the workspace
        // has no profile row yet, in which case the drafter falls back to
        // the pre-E8 behavior of citing register items without evidence.
        const evidenceMap = await this.loadEvidenceForItems([...citedIds]);
        // Split by no-match vs draftable.
        const noMatch = rows.filter((r) => r.citations.length === 0);
        const draftable = rows.filter((r) => r.citations.length > 0);
        const drafts = [];
        // No-match rows: honest disclaimer per plan §D4.
        for (const r of noMatch) {
            drafts.push({
                answer_id: r.answer.answer_id,
                question_id: r.question.question_id,
                draft_text: NO_MATCH_DISCLAIMER,
                draft_confidence: 'low',
                draft_origin: 'ai_inferred',
                drafted_by: 'llm',
                no_match: true,
                provider_id: null,
                model_id: null,
            });
        }
        // Batched LLM draft for the draftable rows.
        for (let i = 0; i < draftable.length; i += DRAFT_BATCH_SIZE) {
            const batch = draftable.slice(i, i + DRAFT_BATCH_SIZE);
            const batchResults = await this.draftBatch(batch, itemLookup, evidenceMap);
            for (const d of batchResults)
                drafts.push(d);
        }
        await this.persistDrafts(questionnaireId, drafts);
        await svc.updateStatus(questionnaireId, 'in_review');
        return {
            drafts,
            no_match_count: drafts.filter((d) => d.no_match).length,
        };
    }
    // ---- internals -----------------------------------------------------
    async loadAnswers(questionnaireId) {
        const res = await this.db.query(`SELECT
          qa.answer_id, qa.questionnaire_id, qa.question_id,
          qa.draft_text, qa.draft_confidence, qa.drafted_by,
          qa.citations_json, qa.preparer_text,
          qa.preparer_edited_at, qa.preparer_edited_by_user_id,
          qa.approved_at, qa.approved_by_user_id,
          qa.needs_human_review,
          qa.approval_note, qa.change_request_reason,
          qa.provider_id, qa.model_id,
          qa.created_at AS answer_created_at,
          qa.updated_at AS answer_updated_at,
          qq.question_id AS q_question_id, qq.seq_no, qq.section,
          qq.question_type, qq.text, qq.extracted_via, qq.draft_origin,
          qq.created_at AS question_created_at
         FROM questionnaire_answers qa
         JOIN questionnaire_questions qq
           ON qq.question_id = qa.question_id
         JOIN questionnaires q
           ON q.questionnaire_id = qa.questionnaire_id
        WHERE q.workspace_id = ? AND qa.questionnaire_id = ?
        ORDER BY qq.seq_no`, [this.db.workspaceId, questionnaireId]);
        const out = [];
        for (const row of res.results) {
            let citations = [];
            try {
                const parsed = JSON.parse(row.citations_json);
                if (Array.isArray(parsed))
                    citations = parsed;
            }
            catch {
                /* empty citations */
            }
            const answer = {
                answer_id: row.answer_id,
                questionnaire_id: row.questionnaire_id,
                question_id: row.question_id,
                draft_text: row.draft_text,
                draft_confidence: row.draft_confidence,
                drafted_by: row.drafted_by,
                citations_json: row.citations_json,
                preparer_text: row.preparer_text,
                preparer_edited_at: row.preparer_edited_at,
                preparer_edited_by_user_id: row.preparer_edited_by_user_id,
                approved_at: row.approved_at,
                approved_by_user_id: row.approved_by_user_id,
                needs_human_review: row.needs_human_review,
                approval_note: row.approval_note,
                change_request_reason: row.change_request_reason,
                provider_id: row.provider_id,
                model_id: row.model_id,
                created_at: row.answer_created_at,
                updated_at: row.answer_updated_at,
            };
            const question = {
                question_id: row.question_id,
                questionnaire_id: row.questionnaire_id,
                seq_no: row.seq_no,
                section: row.section,
                question_type: row.question_type,
                text: row.text,
                extracted_via: row.extracted_via,
                draft_origin: row.draft_origin,
                // Not selected by this query — the drafter only needs
                // question_type/text, not assignment (PR-P2-4).
                assigned_to_user_id: null,
                assigned_at: null,
                created_at: row.question_created_at,
            };
            out.push({ answer, question, citations });
        }
        return out;
    }
    /**
     * E8: load an evidence snapshot per cited register item, scoped to
     * this workspace's profile. Returns an empty map when the workspace
     * has no profile row (the drafter still runs; downgrade logic below
     * treats empty as "missing evidence" and downgrades accordingly).
     */
    async loadEvidenceForItems(itemIds) {
        const out = new Map();
        if (itemIds.length === 0)
            return out;
        const raw = this.db.unscoped();
        const profileRes = await raw.query(`SELECT profile_id FROM customer_profiles WHERE workspace_id = ? LIMIT 1`, [this.db.workspaceId]);
        const profileId = profileRes.results[0]?.profile_id;
        if (!profileId)
            return out;
        const now = new Date();
        for (const itemId of itemIds) {
            // Bindings scoped to the whole profile view (systemId=null on the
            // helper returns profile-wide bindings only; the drafter answer is
            // profile-level, not system-level). E7's per-system rollup is
            // consumed via rollUpMaturityForItem for the maturity signal.
            const bindings = await listBindingSummariesForItem(raw, profileId, itemId, null, now);
            // Pull artifact metadata for the top-N most recently attested.
            const contributing = bindings
                .filter((b) => b.type !== 'freeform')
                .sort((a, b) => attestedRank(b) - attestedRank(a))
                .slice(0, EVIDENCE_CITATIONS_PER_ITEM);
            const snapshots = [];
            for (const b of contributing) {
                const meta = await raw.query(`SELECT ea.title, ea.version_label,
                  eb.relevant_excerpt, eb.section_ref,
                  ea.artifact_ref, ea.artifact_ref_kind
             FROM evidence_bindings eb
             JOIN evidence_artifacts ea ON ea.artifact_id = eb.artifact_id
            WHERE eb.binding_id = ?`, [b.binding_id]);
                const m = meta.results[0];
                const docUrl = m?.artifact_ref_kind === 'url' ? m.artifact_ref : null;
                snapshots.push({
                    type: b.type,
                    artifact_title: m?.title ?? 'Untitled artifact',
                    version_label: m?.version_label ?? null,
                    last_attested_at: b.last_attested_at,
                    freshness: deriveFreshness(b.last_attested_at, b.attestation_cadence_months, now),
                    section_ref: m?.section_ref ?? null,
                    relevant_excerpt: m?.relevant_excerpt ?? null,
                    document_url: docUrl,
                });
            }
            const rollup = await rollUpMaturityForItem(raw, profileId, itemId, now);
            const has_stale = snapshots.some((s) => s.freshness === 'overdue' || s.freshness === 'never');
            out.set(itemId, {
                bindings: snapshots,
                effective_maturity_level: rollup.effective_level,
                derived_maturity_level: rollup.derived_level,
                override_active: rollup.override_level !== null,
                override_reason: rollup.override_reason,
                has_missing_evidence: bindings.length === 0,
                has_stale_evidence: has_stale,
            });
        }
        return out;
    }
    async loadRiskItems(ids) {
        const out = new Map();
        if (ids.length === 0)
            return out;
        // Chunk to keep parameter count sane (D1's limit is 100 per statement).
        const CHUNK = 50;
        for (let i = 0; i < ids.length; i += CHUNK) {
            const chunk = ids.slice(i, i + CHUNK);
            // d1-in-list: bounded — CHUNK (50), no other parameters
            const placeholders = chunk.map(() => '?').join(',');
            const res = await this.db.unscoped().query(`SELECT item_id, title, description, framework_refs
           FROM risk_items
          WHERE item_id IN (${placeholders})`, chunk);
            for (const row of res.results)
                out.set(row.item_id, row);
        }
        return out;
    }
    async draftBatch(batch, itemLookup, evidenceMap) {
        // Composed payload — one entry per question with its citation snippets
        // + E8 evidence snapshot per citation (top bindings + maturity signals).
        const payload = batch.map((row) => ({
            question_id: row.question.question_id,
            section: row.question.section,
            question: row.question.text,
            citations: row.citations
                .map((c) => {
                const item = itemLookup.get(c.risk_item_id);
                if (!item)
                    return null;
                const evidence = evidenceMap.get(c.risk_item_id);
                return {
                    risk_item_id: item.item_id,
                    title: item.title,
                    excerpt: truncate(item.description, 400),
                    frameworks: safeParseArray(item.framework_refs),
                    relevance: Number(c.relevance.toFixed(2)),
                    // E8 evidence hints. Omitted entirely when no profile row
                    // exists so the LLM doesn't fabricate empty-bracket citations.
                    evidence: evidence
                        ? {
                            effective_maturity_level: evidence.effective_maturity_level,
                            maturity_override: evidence.override_active,
                            bindings: evidence.bindings.map((b) => ({
                                type: b.type,
                                artifact_title: b.artifact_title,
                                version_label: b.version_label,
                                last_attested_at: b.last_attested_at,
                                freshness: b.freshness,
                                section_ref: b.section_ref,
                                relevant_excerpt: b.relevant_excerpt ? truncate(b.relevant_excerpt, 500) : null,
                            })),
                        }
                        : null,
                };
            })
                .filter((c) => c !== null),
        }));
        // No LLM (the gateway refused: federal mode / a raised class with no eligible
        // provider — parity questionnaire_drafting → deterministic): a stitched
        // citation-list draft so the pipeline still produces something visible.
        // Preparer edits it into shape. Not an LLM output — provider_id/model_id stay null.
        const citationOnly = () => batch.map((row) => ({
            answer_id: row.answer.answer_id,
            question_id: row.question.question_id,
            draft_text: composeCitationOnlyDraft(row, itemLookup, evidenceMap),
            draft_confidence: downgradeConfidenceForRow(row, evidenceMap),
            draft_origin: deriveDraftOriginForRow(row, evidenceMap),
            drafted_by: 'llm',
            no_match: false,
            provider_id: null,
            model_id: null,
        }));
        let result = [];
        // V3-0-17 / ADR-0020: model_id comes straight off this batch's
        // AIExtractionResult; provider_id is the registry id the provider
        // instance declares (see IAIProvider.providerId). Both stay null
        // below on any row that didn't actually get an LLM-produced draft.
        let batchModelId = null;
        let batchProviderId = null;
        // Taint (ADR-0073 §2): the payload carries questions, register text and, when
        // present, evidence excerpts and artifact titles — its class is the max of those.
        const from = ['questionnaire.question_text', 'register.risk_item_text'];
        if (payload.some((p) => p.citations.some((c) => c && c.evidence)))
            from.push('evidence.artifact_text');
        try {
            const extractRes = await this.llm.run({
                task: 'questionnaire.draft', template: DRAFT_TEMPLATE,
                input: derive('questionnaire.draft_payload', JSON.stringify(payload), from),
                schemaHint: DRAFT_SCHEMA,
            });
            if (extractRes.status !== 'ok')
                return citationOnly();
            batchModelId = extractRes.model_id ?? null;
            batchProviderId = extractRes.provider_id;
            if (Array.isArray(extractRes.output.results))
                result = extractRes.output.results;
        }
        catch {
            // Batch failed — every question in the batch falls back to citation-only.
        }
        const byId = new Map();
        for (const r of result) {
            const qid = typeof r.question_id === 'string' ? r.question_id : null;
            const draft = typeof r.draft_text === 'string' ? r.draft_text.trim() : '';
            if (qid && draft)
                byId.set(qid, draft);
        }
        return batch.map((row) => {
            const llmDraft = byId.get(row.question.question_id);
            const usedLlm = Boolean(llmDraft && llmDraft.length > 0);
            const draftText = usedLlm
                ? llmDraft
                : composeCitationOnlyDraft(row, itemLookup, evidenceMap);
            return {
                answer_id: row.answer.answer_id,
                question_id: row.question.question_id,
                draft_text: draftText,
                draft_confidence: downgradeConfidenceForRow(row, evidenceMap),
                draft_origin: deriveDraftOriginForRow(row, evidenceMap),
                provider_id: usedLlm ? batchProviderId : null,
                model_id: usedLlm ? batchModelId : null,
                drafted_by: 'llm',
                no_match: false,
            };
        });
    }
    async persistDrafts(questionnaireId, drafts) {
        if (drafts.length === 0)
            return;
        const now = new Date().toISOString();
        for (const d of drafts) {
            // Scoped UPDATE — SQL mentions workspace_id + includes it in params so
            // the workspace-scoped wrapper accepts. Matches the extractor's
            // INSERT..SELECT pattern. E8: also persists draft_confidence so the
            // matcher's initial value gets overwritten with the evidence-aware
            // downgraded value. V3-0-17: provider_id/model_id (migration 062)
            // record which registered provider actually produced draft_text.
            const res = await this.db.execute(`UPDATE questionnaire_answers
            SET draft_text = ?, drafted_by = ?, draft_confidence = ?,
                provider_id = ?, model_id = ?, updated_at = ?
          WHERE answer_id = ?
            AND questionnaire_id IN (
              SELECT questionnaire_id FROM questionnaires
               WHERE questionnaire_id = ? AND workspace_id = ?
            )`, [
                d.draft_text,
                d.drafted_by,
                d.draft_confidence,
                d.provider_id,
                d.model_id,
                now,
                d.answer_id,
                questionnaireId,
                this.db.workspaceId,
            ]);
            if (!res.meta?.changes) {
                throw new Error(`Drafter.persistDrafts: answer ${d.answer_id} not in workspace ${this.db.workspaceId} (or questionnaire ${questionnaireId} was deleted mid-run)`);
            }
            // Write draft_origin to the question row (questionnaire_questions table).
            // Column exists via migration 039; this is the first writer.
            await this.db.execute(`UPDATE questionnaire_questions
            SET draft_origin = ?
          WHERE question_id = ?
            AND questionnaire_id IN (
              SELECT questionnaire_id FROM questionnaires
               WHERE questionnaire_id = ? AND workspace_id = ?
            )`, [d.draft_origin, d.question_id, questionnaireId, this.db.workspaceId]);
        }
    }
}
// ---- pure helpers ----------------------------------------------------
function truncate(s, max) {
    if (!s)
        return '';
    return s.length <= max ? s : `${s.slice(0, max)}…`;
}
function safeParseArray(raw) {
    if (!raw)
        return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(String) : [];
    }
    catch {
        return [];
    }
}
function composeCitationOnlyDraft(row, itemLookup, evidenceMap) {
    const parts = row.citations
        .map((c) => {
        const item = itemLookup.get(c.risk_item_id);
        if (!item)
            return null;
        const evidence = evidenceMap.get(c.risk_item_id);
        if (!evidence || evidence.bindings.length === 0) {
            return `[${item.item_id}] ${item.title}`;
        }
        const artifactLines = evidence.bindings.map((b) => {
            const pieces = [b.artifact_title];
            if (b.version_label)
                pieces.push(b.version_label);
            if (b.freshness === 'overdue')
                pieces.push('review overdue');
            else if (b.freshness === 'never')
                pieces.push('never attested');
            else if (b.last_attested_at)
                pieces.push(`last attested ${b.last_attested_at.slice(0, 10)}`);
            return pieces.join(', ');
        });
        const overrideNote = evidence.override_active
            ? ` (maturity override L${evidence.effective_maturity_level})`
            : '';
        return `[${item.item_id}] ${item.title}: ${artifactLines.join('; ')}${overrideNote}`;
    })
        .filter(Boolean);
    if (parts.length === 0)
        return 'Preparer to draft manually — LLM produced no output.';
    return `Draft pending human review. Relevant register items: ${parts.join('; ')}.`;
}
export function downgradeConfidence(base, signals) {
    const shouldDowngrade = signals.hasStaleEvidence || signals.hasMissingEvidence || signals.hasOverride;
    if (!shouldDowngrade)
        return base;
    if (base === 'high')
        return 'medium';
    if (base === 'medium')
        return 'low';
    return 'low';
}
/**
 * Derive the draft_origin classification for a question's answer.
 *
 * Per questionnaire-evidence-plan.md §3:
 *   attested    — all cited items have current, non-overridden evidence
 *   declared    — at least one cited item has evidence, but gaps exist
 *   ai_inferred — no cited items have evidence, or no citations at all
 */
export function deriveDraftOrigin(signals) {
    if (!signals.hasCitations || !signals.hasAnyEvidence)
        return 'ai_inferred';
    if (signals.allEvidenceCurrent && !signals.hasOverride)
        return 'attested';
    return 'declared';
}
function downgradeConfidenceForRow(row, evidenceMap) {
    const base = row.answer.draft_confidence ?? 'low';
    const signals = collectDowngradeSignals(row, evidenceMap);
    return downgradeConfidence(base, signals);
}
function deriveDraftOriginForRow(row, evidenceMap) {
    const hasCitations = row.citations.length > 0;
    let hasAnyEvidence = false;
    let allCurrent = true;
    let hasOverride = false;
    for (const c of row.citations) {
        const ev = evidenceMap.get(c.risk_item_id);
        if (!ev || ev.has_missing_evidence) {
            allCurrent = false;
            continue;
        }
        hasAnyEvidence = true;
        if (ev.has_stale_evidence)
            allCurrent = false;
        if (ev.override_active)
            hasOverride = true;
    }
    return deriveDraftOrigin({ hasCitations, hasAnyEvidence, allEvidenceCurrent: allCurrent, hasOverride });
}
function collectDowngradeSignals(row, evidenceMap) {
    const signals = {
        hasStaleEvidence: false,
        hasMissingEvidence: false,
        hasOverride: false,
    };
    for (const c of row.citations) {
        const ev = evidenceMap.get(c.risk_item_id);
        if (!ev) {
            signals.hasMissingEvidence = true;
            continue;
        }
        if (ev.has_missing_evidence)
            signals.hasMissingEvidence = true;
        if (ev.has_stale_evidence)
            signals.hasStaleEvidence = true;
        if (ev.override_active)
            signals.hasOverride = true;
    }
    return signals;
}
/**
 * Rank a binding by attestation recency so the drafter picks the
 * freshest N bindings to cite. Never-attested bindings sort to the
 * bottom. Not exported — internal to the loadEvidenceForItems helper.
 */
function attestedRank(b) {
    if (!b.last_attested_at)
        return -Infinity;
    return new Date(b.last_attested_at).getTime();
}
//# sourceMappingURL=questionnaire-drafter-service.js.map